import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import {
  apiErrorText,
  econChronumApi,
  useSetEconChronumPositionMutation,
  type ChronumPosition,
  type ChronumPositionBody,
  type ChronumPositionMapRow,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import type { RootState } from '@app/store';
import { isStaleError } from './apiErrors';

export type PositionSave =
  | { phase: 'pending' }
  | { phase: 'saved' }
  /** `stale` — 409: должность изменил кто-то другой, нужно «Перечитать». */
  | { phase: 'error'; message: string; stale: boolean };

export interface PositionSaver {
  /** Что с сохранением категории строки; `undefined` — ничего не происходит. */
  state: (key: string) => PositionSave | undefined;
  /** Выбрать категорию: сразу в списке, на сервер — чуть позже, последним выбором. */
  choose: (position: ChronumPosition, category: string | null) => void;
  /** Повторить несохранившийся выбор. */
  retry: (key: string) => void;
  /** Перечитать должность после 409: список и журнал — с сервера. */
  reread: (key: string) => void;
  /**
   * Сохранить комментарий (без поля категории — категория не трогается).
   * Ошибка — исключением: её показывает форма комментария.
   */
  saveComment: (position: ChronumPosition, comment: string) => Promise<void>;
  /** Последнее, что сказать скринридеру. */
  announcement: string;
}

/** Пауза перед отправкой: стрелки в закрытом списке меняют выбор на каждом нажатии. */
const DELAY_MS = 450;
/** Сколько держать «Сохранено» у строки. */
const SAVED_MS = 2500;

interface Wanted {
  name: string;
  category: string | null;
}

const POSITIONS = { type: 'EconChronum', id: 'POSITIONS' } as const;
const history = (key: string) => ({ type: 'EconChronum', id: `HISTORY:position:${key}` }) as const;

/**
 * Категории ФОТ сохраняются сами, без кнопки. Выбор сразу виден в списке
 * (кеш RTK), а на сервер уходит через паузу — последним значением и с
 * версией должности (`expected_updated_at`): чужая правка не перетирается, а
 * даёт 409 и «Перечитать». По каждой должности отправки идут строго по одной:
 * ответ на старый выбор не перетрёт новый. Недосланное при уходе с вкладки
 * уходит сразу.
 */
export function usePositionSaver(labelOf: (category: string | null) => string): PositionSaver {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [put] = useSetEconChronumPositionMutation();
  const putRef = useRef(put);
  putRef.current = put;
  const labelRef = useRef(labelOf);
  labelRef.current = labelOf;
  /**
   * Версия сопоставления — из кеша списка в момент отправки: туда же сразу
   * ложится версия из ответа прошлой записи, до перерисовки экрана.
   */
  const versionRef = useRef((key: string): string | null => {
    const list = econChronumApi.endpoints.listEconChronumPositions.select()(store.getState()).data;
    return list?.items.find((i) => i.key === key)?.updated_at ?? null;
  });

  const [states, setStates] = useState<Record<string, PositionSave>>({});
  const [announcement, setAnnouncement] = useState('');
  const wanted = useRef(new Map<string, Wanted>());
  const failed = useRef(new Map<string, Wanted>());
  const inFlight = useRef(new Set<string>());
  const timers = useRef(new Map<string, number>());
  const savedTimers = useRef(new Map<string, number>());

  const setState = useCallback((key: string, next: PositionSave | undefined) => {
    setStates((prev) => {
      const copy = { ...prev };
      if (next) copy[key] = next;
      else delete copy[key];
      return copy;
    });
  }, []);

  /** Выбор — сразу в список (до ответа сервера). */
  const patchCategory = useCallback(
    (key: string, category: string | null) => {
      dispatch(
        econChronumApi.util.updateQueryData('listEconChronumPositions', undefined, (draft) => {
          const item = draft.items.find((i) => i.key === key);
          if (item) item.payroll_category = category;
        }),
      );
    },
    [dispatch],
  );

  /** Ответ сервера — версия, комментарий и автор правки в список. */
  const applySaved = useCallback(
    (key: string, row: ChronumPositionMapRow | null, keepCategory: boolean) => {
      if (!row) return;
      dispatch(
        econChronumApi.util.updateQueryData('listEconChronumPositions', undefined, (draft) => {
          const item = draft.items.find((i) => i.key === key);
          if (!item) return;
          // Пока ответ шёл, человек мог выбрать другую категорию — её не трогаем.
          if (!keepCategory) item.payroll_category = row.payroll_category;
          item.comment = row.comment;
          item.updated_at = row.updated_at;
          item.updated_by_name = row.updated_by_name;
        }),
      );
    },
    [dispatch],
  );

  const send = useCallback(
    async (key: string): Promise<void> => {
      if (inFlight.current.has(key)) return;
      const w = wanted.current.get(key);
      if (!w) return;
      wanted.current.delete(key);
      inFlight.current.add(key);
      const body: ChronumPositionBody = {
        name: w.name,
        payroll_category: w.category,
        expected_updated_at: versionRef.current(key),
      };
      try {
        const res = await putRef.current({ key, body }).unwrap();
        inFlight.current.delete(key);
        failed.current.delete(key);
        applySaved(key, res.position, wanted.current.has(key));
        if (wanted.current.has(key)) {
          void send(key);
          return;
        }
        setState(key, { phase: 'saved' });
        setAnnouncement(`${w.name}: сохранено — ${labelRef.current(w.category)}.`);
        window.clearTimeout(savedTimers.current.get(key));
        savedTimers.current.set(
          key,
          window.setTimeout(() => {
            savedTimers.current.delete(key);
            setStates((prev) => {
              if (prev[key]?.phase !== 'saved') return prev;
              const copy = { ...prev };
              delete copy[key];
              return copy;
            });
          }, SAVED_MS),
        );
      } catch (err) {
        inFlight.current.delete(key);
        const stale = isStaleError(err);
        if (wanted.current.has(key) && !stale) {
          void send(key);
          return;
        }
        // Чужая правка: новые выборы до «Перечитать» на сервер не идут.
        wanted.current.delete(key);
        const message = apiErrorText(err, 'Категория не сохранилась.');
        failed.current.set(key, w);
        setState(key, { phase: 'error', message, stale });
        setAnnouncement(`${w.name}: категория не сохранилась. ${message}`);
        // Не чужая правка — список по серверу: в нём то, что сохранено на самом
        // деле. Чужую правку показывает «Перечитать».
        if (!stale) dispatch(econChronumApi.util.invalidateTags([POSITIONS]));
      }
    },
    [applySaved, dispatch, setState],
  );

  const schedule = useCallback(
    (key: string, w: Wanted) => {
      patchCategory(key, w.category);
      wanted.current.set(key, w);
      failed.current.delete(key);
      setState(key, { phase: 'pending' });
      window.clearTimeout(timers.current.get(key));
      timers.current.set(
        key,
        window.setTimeout(() => {
          timers.current.delete(key);
          void send(key);
        }, DELAY_MS),
      );
    },
    [patchCategory, send, setState],
  );

  const choose = useCallback(
    (position: ChronumPosition, category: string | null) =>
      schedule(position.key, { name: position.name, category }),
    [schedule],
  );

  const retry = useCallback(
    (key: string) => {
      const w = failed.current.get(key);
      if (w) schedule(key, w);
    },
    [schedule],
  );

  const reread = useCallback(
    (key: string) => {
      failed.current.delete(key);
      setState(key, undefined);
      dispatch(econChronumApi.util.invalidateTags([POSITIONS, history(key)]));
    },
    [dispatch, setState],
  );

  const saveComment = useCallback(
    async (position: ChronumPosition, comment: string) => {
      const res = await putRef
        .current({
          key: position.key,
          body: {
            name: position.name,
            comment,
            expected_updated_at: versionRef.current(position.key),
          },
        })
        .unwrap();
      applySaved(position.key, res.position, true);
      setAnnouncement(
        comment.trim()
          ? `${position.name}: комментарий сохранён.`
          : `${position.name}: комментарий удалён.`,
      );
    },
    [applySaved],
  );

  // Уход с вкладки: недосланное — сразу, «Сохранено» больше показывать некому.
  useEffect(() => {
    const pending = timers.current;
    const saved = savedTimers.current;
    return () => {
      for (const [key, t] of pending) {
        window.clearTimeout(t);
        void send(key);
      }
      pending.clear();
      for (const t of saved.values()) window.clearTimeout(t);
      saved.clear();
    };
  }, [send]);

  return {
    state: (key) => states[key],
    choose,
    retry,
    reread,
    saveComment,
    announcement,
  };
}
